// path: dashboard/src/pages/Head-Of-Operations/HOCReports.jsx

"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts"
import {
  FileText,
  Download,
  Filter,
  Package,
  Truck,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Search,
  FileCheck,
  ChevronDown,
  Calendar,
  DollarSign,
  Users,
  BarChart3,
  Settings,
  MoreHorizontal,
  Grid,
  List,
  FileSpreadsheet,
  Plane,
  MapPin,
  User,
} from "lucide-react"
import HOSidebar from "../../components/MasterSideBar"
import { getRequisitions } from "../../api/procurementApi"
import Topbar from "../../components/TopbarNav"
import TravelRequestCard from "../../components/operationsAssociate/TravelRequestCard"
import RejectModal from "../../components/operationsAssociate/TravelRejectModal"
import DashboardSummary from "../OperationsAssociate/TravelSummarystats"
import AddItineraryModal from "../../components/head-of-Operations/AddItenaryModal"
import useTravelRequests from "../../hooks/useTravelRequest"

const API_BASE_URL = "https://ims.holdthechild.net/api"

const getAuthHeaders = () => {
  const token = localStorage.getItem("token")
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }
}

const HOCReports = () => {
  const [selectedProject, setSelectedProject] = useState("all")
  const [reportType, setReportType] = useState("procurement")
  const [dateRange, setDateRange] = useState("current_month")
  const [customStartDate, setCustomStartDate] = useState("")
  const [customEndDate, setCustomEndDate] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [projects, setProjects] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [viewMode, setViewMode] = useState("grid") // grid or list
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)
  const exportDropdownRef = useRef(null)
  const [sidebarOpen, setSidebarOpen] = useState(false);


  // Travel logistics state
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false)
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false)
  const [selectedRequestId, setSelectedRequestId] = useState(null)
  const [travelFilters, setTravelFilters] = useState({
    status: "",
    dateRange: "",
    destination: "",
  })

  const {
    requests: travelRequests,
    loading: travelLoading,
    error: travelError,
    summary: travelSummary,
    updateRequestStatus,
    addItinerary,
    refreshRequests,
    fetchTravelRequests,
  } = useTravelRequests()

  const [reportData, setReportData] = useState({
    procurement: {
      summary: { total: 0, pending: 0, fulfilled: 0, delayed: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    travel_logistics: {
      summary: { total: 0, pending: 0, approved: 0, rejected: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    requisitions: {
      summary: { total: 0, pending: 0, fulfilled: 0, delayed: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    vendor: {
      summary: { total: 0, active: 0, inactive: 0, pending: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    contract: {
      summary: { total: 0, active: 0, expired: 0, renewing: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
    project_planning: {
      summary: { totalProjects: 0, totalRequirements: 0, totalValue: 0, pending: 0 },
      byStatus: [],
      timeline: [],
      details: [],
    },
  })

  // Fetch projects for the dropdown
  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: getAuthHeaders(),
      })
      const data = await response.json()
      const projectsData = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []

      const formattedProjects = [
        { _id: "all", name: "All Projects" },
        ...projectsData.map((project) => ({
          _id: project._id,
          name: project.projectTitle || project.name || `Project ${project._id}`,
        })),
      ]

      setProjects(formattedProjects)
    } catch (err) {
      console.error("Error fetching projects:", err)
      setProjects([{ _id: "all", name: "All Projects" }])
    }
  }, [])

  // Fetch procurement data
  const fetchProcurementData = useCallback(async () => {
    try {
      const [rfqsRes, rfpsRes, boqsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/RFQs`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/rfps`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/boqs`, { headers: getAuthHeaders() }),
      ])

      const [rfqs, rfps, boqs] = await Promise.all([rfqsRes.json(), rfpsRes.json(), boqsRes.json()])

      // Process and combine data
      const processData = (data, type) =>
        Array.isArray(data)
          ? data.map((item) => ({
            ...item,
            type,
            requestDate: item.issueDate || item.createdAt,
            expectedDate: item.submissionDeadline,
            description: item.projectName || item.notes || "N/A",
            status: mapStatus(item.status),
            value: item.estimatedValue || item.totalValue || "N/A",
          }))
          : []

      const allData = [...processData(rfqs, "RFQ"), ...processData(rfps, "RFP"), ...processData(boqs, "BOQ")]

      return processReportData(allData)
    } catch (err) {
      console.error("Error fetching procurement data:", err)
      return getEmptyReportData()
    }
  }, [])

  // Fetch travel logistics data
  const fetchTravelLogisticsData = useCallback(async () => {
    try {
      // Process travel requests data
      const processedData = travelRequests.map((request) => ({
        ...request,
        type: "Travel Request",
        requestDate: request.createdAt,
        expectedDate: request.departureDate,
        description: request.purpose,
        status: request.status,
        value: request.estimatedCost,
        project: request.project?.projectTitle || "N/A",
        destination: request.destination,
        traveler: request.travelerName,
      }))

      return processTravelReportData(processedData)
    } catch (err) {
      console.error("Error fetching travel logistics data:", err)
      return getEmptyTravelReportData()
    }
  }, [travelRequests])

  // Fetch requisitions data
  const fetchRequisitionsData = useCallback(async () => {
    try {
      const filters = {
        ...(selectedProject !== "all" && { projectId: selectedProject }),
        limit: 1000,
        page: 1,
      }

      const response = await getRequisitions(filters)
      const requisitions = response.data?.data || response.data || []

      const processedData = (Array.isArray(requisitions) ? requisitions : []).map((req) => ({
        ...req,
        type: "Requisition",
        requestDate: req.createdAt,
        expectedDate: req.dueDate,
        description: req.title,
        status: mapRequisitionStatus(req.currentStatus),
        value: req.totalEstimatedValue || "N/A",
        project: req.projectId?.projectTitle || "N/A",
        projectId: req.projectId?._id,
        typeOfProcurement: req.typeOfProcurement || "N/A",
        currentStatus: req.currentStatus || "N/A",
      }))

      return processReportData(processedData)
    } catch (err) {
      console.error("Error fetching requisitions data:", err)
      return getEmptyReportData()
    }
  }, [selectedProject])

  // Fetch vendor data
  const fetchVendorData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vendors`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error("Vendors endpoint not available")
      }

      const data = await response.json()
      const vendors = Array.isArray(data.data) ? data.data : []

      const processedData = vendors.map((vendor) => ({
        ...vendor,
        type: "Vendor",
        requestDate: vendor.createdAt,
        expectedDate: vendor.contractExpiryDate,
        description: vendor.name,
        status: vendor.status,
        value: vendor.totalValue || "N/A",
        project: vendor.projects?.map(p => p.projectTitle).join(", ") || "N/A",
      }))

      return processVendorReportData(processedData)
    } catch (err) {
      console.error("Error fetching vendor data:", err)
      return getEmptyVendorReportData()
    }
  }, [])

  // Fetch contract data - extracted from vendor sub-documents
  const fetchContractData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/vendors`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error("Vendors endpoint not available")
      }

      const data = await response.json()
      const vendors = Array.isArray(data.data) ? data.data : []

      // Extract contracts from all vendors
      const allContracts = []
      vendors.forEach((vendor) => {
        if (vendor.contracts && Array.isArray(vendor.contracts)) {
          vendor.contracts.forEach((contract) => {
            allContracts.push({
              ...contract,
              type: "Contract",
              requestDate: contract.startDate,
              expectedDate: contract.endDate,
              description: contract.title || contract.contractNumber || "Untitled",
              status: contract.status || "active",
              value: contract.value || "N/A",
              vendor: vendor.name || "N/A",
              project: vendor.projects?.map(p => p.projectTitle).join(", ") || "N/A",
            })
          })
        }
      })

      return processContractReportData(allContracts)
    } catch (err) {
      console.error("Error fetching contract data:", err)
      return getEmptyContractReportData()
    }
  }, [])

  // Fetch project planning data
  const fetchProjectPlanningData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/project-planning`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error("Project planning endpoint not available")
      }

      const data = await response.json()
      const projects = Array.isArray(data.data) ? data.data : []

      const processedData = projects.map((project) => ({
        ...project,
        type: "Project Planning",
        requestDate: project.startDate,
        expectedDate: project.endDate,
        description: project.projectTitle,
        status: project.status,
        value: project.budget || "N/A",
        requirements: project.requirements || [],
      }))

      return processProjectPlanningReportData(processedData)
    } catch (err) {
      console.error("Error fetching project planning data:", err)
      return getEmptyProjectPlanningReportData()
    }
  }, [])

  // Map various status values to our standard format
  const mapStatus = (status) => {
    if (!status) return "pending"
    const statusLower = status.toLowerCase()

    if (["completed", "approved", "submitted", "closed"].includes(statusLower)) {
      return "fulfilled"
    }
    if (["draft", "pending", "open"].includes(statusLower)) {
      return "pending"
    }
    if (["delayed", "overdue"].includes(statusLower)) {
      return "delayed"
    }
    return "pending"
  }

  // Map requisition status to our standard format
  const mapRequisitionStatus = (status) => {
    if (!status) return "pending"

    const statusMap = {
      draft: "pending",
      submitted: "pending",
      pending_director_approval: "pending",
      approved_by_director: "fulfilled",
      rejected_by_director: "delayed",
      processing_by_procurement: "pending",
      completed: "fulfilled",
      cancelled: "delayed",
      on_hold: "delayed",
    }

    return statusMap[status] || "pending"
  }

  // Process raw data into report format
  const processReportData = (data) => {
    const summary = {
      total: data.length,
      pending: data.filter((item) => item.status === "pending").length,
      fulfilled: data.filter((item) => item.status === "fulfilled").length,
      delayed: data.filter((item) => item.status === "delayed").length,
    }

    const byStatus = [
      { name: "Pending", value: summary.pending, color: "#f59e0b" },
      { name: "Fulfilled", value: summary.fulfilled, color: "#10b981" },
      { name: "Delayed", value: summary.delayed, color: "#ef4444" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        pending: monthData.filter((item) => item.status === "pending").length,
        fulfilled: monthData.filter((item) => item.status === "fulfilled").length,
        delayed: monthData.filter((item) => item.status === "delayed").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process travel logistics data into report format
  const processTravelReportData = (data) => {
    const summary = {
      total: data.length,
      pending: data.filter((item) => item.status === "pending").length,
      approved: data.filter((item) => item.status === "approved").length,
      rejected: data.filter((item) => item.status === "rejected").length,
    }

    const byStatus = [
      { name: "Pending", value: summary.pending, color: "#f59e0b" },
      { name: "Approved", value: summary.approved, color: "#10b981" },
      { name: "Rejected", value: summary.rejected, color: "#ef4444" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        pending: monthData.filter((item) => item.status === "pending").length,
        approved: monthData.filter((item) => item.status === "approved").length,
        rejected: monthData.filter((item) => item.status === "rejected").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process vendor data into report format
  const processVendorReportData = (data) => {
    const summary = {
      total: data.length,
      active: data.filter((item) => item.status === "active").length,
      inactive: data.filter((item) => item.status === "inactive").length,
      pending: data.filter((item) => item.status === "pending").length,
    }

    const byStatus = [
      { name: "Active", value: summary.active, color: "#10b981" },
      { name: "Inactive", value: summary.inactive, color: "#ef4444" },
      { name: "Pending", value: summary.pending, color: "#f59e0b" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        active: monthData.filter((item) => item.status === "active").length,
        inactive: monthData.filter((item) => item.status === "inactive").length,
        pending: monthData.filter((item) => item.status === "pending").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process contract data into report format
  const processContractReportData = (data) => {
    const summary = {
      total: data.length,
      active: data.filter((item) => item.status === "active").length,
      expired: data.filter((item) => item.status === "expired").length,
      renewing: data.filter((item) => item.status === "renewing").length,
    }

    const byStatus = [
      { name: "Active", value: summary.active, color: "#10b981" },
      { name: "Expired", value: summary.expired, color: "#ef4444" },
      { name: "Renewing", value: summary.renewing, color: "#f59e0b" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        active: monthData.filter((item) => item.status === "active").length,
        expired: monthData.filter((item) => item.status === "expired").length,
        renewing: monthData.filter((item) => item.status === "renewing").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  // Process project planning data into report format
  const processProjectPlanningReportData = (data) => {
    const summary = {
      totalProjects: data.length,
      totalRequirements: data.reduce((sum, project) => sum + (project.requirements?.length || 0), 0),
      totalValue: data.reduce((sum, project) => sum + (project.budget || 0), 0),
      pending: data.filter((item) => item.status === "planning").length,
    }

    const byStatus = [
      { name: "Planning", value: summary.pending, color: "#f59e0b" },
      { name: "Active", value: data.filter((item) => item.status === "active").length, color: "#10b981" },
      { name: "Completed", value: data.filter((item) => item.status === "completed").length, color: "#3b82f6" },
    ]

    // Generate timeline for last 6 months
    const timeline = []
    for (let i = 5; i >= 0; i--) {
      const date = new Date()
      date.setMonth(date.getMonth() - i)
      const monthName = date.toLocaleString("default", { month: "short" })

      const monthData = data.filter((item) => {
        if (!item.requestDate) return false
        const itemDate = new Date(item.requestDate)
        return itemDate.getMonth() === date.getMonth() && itemDate.getFullYear() === date.getFullYear()
      })

      timeline.push({
        month: monthName,
        planning: monthData.filter((item) => item.status === "planning").length,
        active: monthData.filter((item) => item.status === "active").length,
        completed: monthData.filter((item) => item.status === "completed").length,
        total: monthData.length,
      })
    }

    return {
      summary,
      byStatus,
      timeline,
      details: data,
    }
  }

  const getEmptyReportData = () => ({
    summary: { total: 0, pending: 0, fulfilled: 0, delayed: 0 },
    byStatus: [
      { name: "Pending", value: 0, color: "#f59e0b" },
      { name: "Fulfilled", value: 0, color: "#10b981" },
      { name: "Delayed", value: 0, color: "#ef4444" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyTravelReportData = () => ({
    summary: { total: 0, pending: 0, approved: 0, rejected: 0 },
    byStatus: [
      { name: "Pending", value: 0, color: "#f59e0b" },
      { name: "Approved", value: 0, color: "#10b981" },
      { name: "Rejected", value: 0, color: "#ef4444" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyVendorReportData = () => ({
    summary: { total: 0, active: 0, inactive: 0, pending: 0 },
    byStatus: [
      { name: "Active", value: 0, color: "#10b981" },
      { name: "Inactive", value: 0, color: "#ef4444" },
      { name: "Pending", value: 0, color: "#f59e0b" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyContractReportData = () => ({
    summary: { total: 0, active: 0, expired: 0, renewing: 0 },
    byStatus: [
      { name: "Active", value: 0, color: "#10b981" },
      { name: "Expired", value: 0, color: "#ef4444" },
      { name: "Renewing", value: 0, color: "#f59e0b" },
    ],
    timeline: [],
    details: [],
  })

  const getEmptyProjectPlanningReportData = () => ({
    summary: { totalProjects: 0, totalRequirements: 0, totalValue: 0, pending: 0 },
    byStatus: [
      { name: "Planning", value: 0, color: "#f59e0b" },
      { name: "Active", value: 0, color: "#10b981" },
      { name: "Completed", value: 0, color: "#3b82f6" },
    ],
    timeline: [],
    details: [],
  })

  // Load report data based on type and filters
  const loadReportData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      if (reportType === "procurement") {
        const data = await fetchProcurementData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            pending: data.details.filter((item) => item.status === "pending").length,
            fulfilled: data.details.filter((item) => item.status === "fulfilled").length,
            delayed: data.details.filter((item) => item.status === "delayed").length,
          }

          data.byStatus = [
            { name: "Pending", value: filteredSummary.pending, color: "#f59e0b" },
            { name: "Fulfilled", value: filteredSummary.fulfilled, color: "#10b981" },
            { name: "Delayed", value: filteredSummary.delayed, color: "#ef4444" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "travel_logistics") {
        const data = await fetchTravelLogisticsData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            pending: data.details.filter((item) => item.status === "pending").length,
            approved: data.details.filter((item) => item.status === "approved").length,
            rejected: data.details.filter((item) => item.status === "rejected").length,
          }

          data.byStatus = [
            { name: "Pending", value: filteredSummary.pending, color: "#f59e0b" },
            { name: "Approved", value: filteredSummary.approved, color: "#10b981" },
            { name: "Rejected", value: filteredSummary.rejected, color: "#ef4444" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "requisitions") {
        const data = await fetchRequisitionsData()
        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "vendor") {
        const data = await fetchVendorData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            active: data.details.filter((item) => item.status === "active").length,
            inactive: data.details.filter((item) => item.status === "inactive").length,
            pending: data.details.filter((item) => item.status === "pending").length,
          }

          data.byStatus = [
            { name: "Active", value: filteredSummary.active, color: "#10b981" },
            { name: "Inactive", value: filteredSummary.inactive, color: "#ef4444" },
            { name: "Pending", value: filteredSummary.pending, color: "#f59e0b" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "contract") {
        const data = await fetchContractData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            total: data.details.length,
            active: data.details.filter((item) => item.status === "active").length,
            expired: data.details.filter((item) => item.status === "expired").length,
            renewing: data.details.filter((item) => item.status === "renewing").length,
          }

          data.byStatus = [
            { name: "Active", value: filteredSummary.active, color: "#10b981" },
            { name: "Expired", value: filteredSummary.expired, color: "#ef4444" },
            { name: "Renewing", value: filteredSummary.renewing, color: "#f59e0b" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      } else if (reportType === "project_planning") {
        const data = await fetchProjectPlanningData()

        // Apply project filter
        if (selectedProject !== "all") {
          data.details = data.details.filter(
            (item) => item.projectId === selectedProject || item.project === selectedProject,
          )

          // Recalculate summary after filtering
          const filteredSummary = {
            totalProjects: data.details.length,
            totalRequirements: data.details.reduce((sum, project) => sum + (project.requirements?.length || 0), 0),
            totalValue: data.details.reduce((sum, project) => sum + (project.budget || 0), 0),
            pending: data.details.filter((item) => item.status === "planning").length,
          }

          data.byStatus = [
            { name: "Planning", value: filteredSummary.pending, color: "#f59e0b" },
            { name: "Active", value: data.details.filter((item) => item.status === "active").length, color: "#10b981" },
            { name: "Completed", value: data.details.filter((item) => item.status === "completed").length, color: "#3b82f6" },
          ]

          data.summary = filteredSummary
        }

        setReportData((prev) => ({
          ...prev,
          [reportType]: data,
        }))
      }
    } catch (err) {
      setError(`Failed to load ${reportType} report: ${err.message}`)
      if (reportType === "vendor") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyVendorReportData(),
        }))
      } else if (reportType === "contract") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyContractReportData(),
        }))
      } else if (reportType === "project_planning") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyProjectPlanningReportData(),
        }))
      } else if (reportType === "travel_logistics") {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyTravelReportData(),
        }))
      } else {
        setReportData((prev) => ({
          ...prev,
          [reportType]: getEmptyReportData(),
        }))
      }
    } finally {
      setLoading(false)
    }
  }, [reportType, selectedProject, fetchProcurementData, fetchTravelLogisticsData, fetchRequisitionsData, fetchVendorData, fetchContractData, fetchProjectPlanningData])

  // Initialize data
  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  useEffect(() => {
    loadReportData()
  }, [loadReportData])

  // Travel logistics handlers
  const handleApprove = async (id) => {
    await updateRequestStatus(id, "approved")
  }

  const handleReject = (id) => {
    setSelectedRequestId(id)
    setIsRejectModalOpen(true)
  }

  const handleRejectSubmit = async (reason) => {
    await updateRequestStatus(selectedRequestId, "rejected", { rejectionReason: reason })
    setIsRejectModalOpen(false)
    setSelectedRequestId(null)
  }

  const handleAddItinerary = (id) => {
    setSelectedRequestId(id)
    setIsItineraryModalOpen(true)
  }

  const handleItinerarySubmit = async (itineraryData) => {
    await addItinerary(selectedRequestId, {
      ...itineraryData,
      bookedBy: "operations_associate_id",
    })
    setIsItineraryModalOpen(false)
    setSelectedRequestId(null)
  }

  const handleReviewReport = (id) => {
    console.log("Review report for request:", id)
  }

  const handleTravelFilterChange = (newFilters) => {
    setTravelFilters(newFilters)
    fetchTravelRequests(newFilters)
  }

  // Export functionality
  const exportReport = (format) => {
    const currentData = reportData[reportType]
    const timestamp = new Date().toISOString().split("T")[0]
    const filename = `${reportType}-report-${selectedProject}-${timestamp}`

    try {
      if (format === "json") {
        const exportData = {
          reportType,
          project: projects.find((p) => p._id === selectedProject)?.name || "All Projects",
          dateRange,
          generatedAt: new Date().toISOString(),
          summary: currentData.summary,
          details: currentData.details,
        }

        const dataStr = JSON.stringify(exportData, null, 2)
        const dataBlob = new Blob([dataStr], { type: "application/json" })
        const url = URL.createObjectURL(dataBlob)
        const link = document.createElement("a")
        link.href = url
        link.download = `${filename}.json`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } else if (format === "csv") {
        let headers
        if (reportType === "requisitions") {
          headers = [
            "ID",
            "Title",
            "Project",
            "Type",
            "Status",
            "Created Date",
            "Due Date",
            "Estimated Value",
            "Current Status",
          ]
        } else if (reportType === "vendor") {
          headers = [
            "ID",
            "Name",
            "Email",
            "Category",
            "Status",
            "Created Date",
            "Contract Expiry",
            "Total Value",
          ]
        } else if (reportType === "contract") {
          headers = [
            "ID",
            "Title",
            "Vendor",
            "Type",
            "Status",
            "Start Date",
            "End Date",
            "Value",
          ]
        } else if (reportType === "project_planning") {
          headers = [
            "ID",
            "Project Title",
            "Status",
            "Start Date",
            "End Date",
            "Budget",
            "Requirements Count",
          ]
        } else if (reportType === "travel_logistics") {
          headers = [
            "ID",
            "Traveler",
            "Destination",
            "Purpose",
            "Status",
            "Departure Date",
            "Return Date",
            "Estimated Cost",
          ]
        } else {
          headers = [
            "ID",
            "Type",
            "Project",
            "Description",
            "Status",
            "Request Date",
            "Expected Date",
            reportType === "procurement" ? "Value" : "Cost",
          ]
        }

        const csvContent = [
          headers.join(","),
          ...currentData.details.map((item) => {
            if (reportType === "requisitions") {
              return [
                item._id || item.id,
                `"${(item.title || "").replace(/"/g, '""')}"`,
                item.project || "N/A",
                item.typeOfProcurement || "N/A",
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
                item.currentStatus || "N/A",
              ].join(",")
            } else if (reportType === "vendor") {
              return [
                item._id || item.id,
                `"${(item.name || "").replace(/"/g, '""')}"`,
                item.email || "N/A",
                item.category || "N/A",
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
              ].join(",")
            } else if (reportType === "contract") {
              return [
                item._id || item.id,
                `"${(item.title || "").replace(/"/g, '""')}"`,
                item.vendor || "N/A",
                item.type || "N/A",
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
              ].join(",")
            } else if (reportType === "project_planning") {
              return [
                item._id || item.id,
                `"${(item.projectTitle || "").replace(/"/g, '""')}"`,
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
                item.requirements?.length || 0,
              ].join(",")
            } else if (reportType === "travel_logistics") {
              return [
                item._id || item.id,
                `"${(item.traveler || "").replace(/"/g, '""')}"`,
                `"${(item.destination || "").replace(/"/g, '""')}"`,
                `"${(item.description || "").replace(/"/g, '""')}"`,
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || "N/A",
              ].join(",")
            } else {
              return [
                item._id || item.id,
                item.type,
                item.project || "N/A",
                `"${(item.description || "").replace(/"/g, '""')}"`,
                item.status,
                item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "",
                item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "",
                item.value || item.cost || "N/A",
              ].join(",")
            }
          }),
        ].join("\n")

        const csvBlob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(csvBlob)
        const link = document.createElement("a")
        link.href = url
        link.download = `${filename}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } else {
        // For PDF and Excel, you'd typically call a backend service
        alert(`${format.toUpperCase()} export would be implemented with a backend service for proper formatting.`)
      }

      // Close dropdown after export
      setExportDropdownOpen(false)
    } catch (err) {
      setError(`Failed to export report: ${err.message}`)
    }
  }

  // Helper functions for UI
  const getStatusIcon = (status) => {
    switch (status) {
      case "fulfilled":
      case "active":
      case "completed":
      case "approved":
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case "pending":
      case "planning":
        return <Clock className="w-4 h-4 text-yellow-500" />
      case "delayed":
      case "expired":
      case "inactive":
      case "rejected":
        return <AlertCircle className="w-4 h-4 text-red-500" />
      default:
        return <XCircle className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "fulfilled":
      case "active":
      case "completed":
      case "approved":
        return "bg-green-100 text-green-800 border-green-200"
      case "pending":
      case "planning":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "delayed":
      case "expired":
      case "inactive":
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  // Filter and paginate data
  const currentData = reportData[reportType]

  const filteredDetails = currentData.details.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.projectTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.project?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reportType === "travel_logistics" && (
        item.traveler?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.destination?.toLowerCase().includes(searchTerm.toLowerCase())
      ))

    const matchesStatus = statusFilter === "all" || item.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const totalPages = Math.ceil(filteredDetails.length / itemsPerPage)
  const paginatedData = filteredDetails.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setExportDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Add a handleLogout function
  const handleLogout = () => {
    // Clear token and redirect to login or home page
    localStorage.removeItem("token")
    window.location.href = "/login"
  }

  // Get report title based on type
  const getReportTitle = () => {
    switch (reportType) {
      case "procurement": return "Procurement Analytics"
      case "travel_logistics": return "Travel Logistics Management"
      case "requisitions": return "Requisitions Analytics"
      case "vendor": return "Vendor Management Reports"
      case "contract": return "Contract Management Reports"
      case "project_planning": return "Project Procurement Planning"
      default: return "Reports Dashboard"
    }
  }

  // Get KPI cards based on report type
  const getKPICards = () => {
    const summary = currentData.summary

    if (reportType === "vendor") {
      return [
        {
          title: "Total Vendors",
          value: summary.total,
          icon: Users,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Active Vendors",
          value: summary.active,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Inactive Vendors",
          value: summary.inactive,
          icon: XCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
        {
          title: "Pending Vendors",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
      ]
    } else if (reportType === "contract") {
      // Calculate expiring soon contracts
      const now = new Date()
      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(now.getDate() + 30)

      const expiringSoon = currentData.details.filter(item => {
        if (!item.endDate) return false
        const endDate = new Date(item.endDate)
        return endDate > now && endDate <= thirtyDaysFromNow
      }).length

      return [
        {
          title: "Total Contracts",
          value: summary.total,
          icon: FileText,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Active Contracts",
          value: summary.active,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Expired Contracts",
          value: summary.expired,
          icon: XCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
        {
          title: "Expiring Soon",
          value: expiringSoon,
          icon: AlertTriangle,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
      ]
    } else if (reportType === "project_planning") {
      return [
        {
          title: "Total Projects",
          value: summary.totalProjects,
          icon: BarChart3,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Total Requirements",
          value: summary.totalRequirements,
          icon: FileCheck,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Total Value",
          value: `$${(summary.totalValue / 1000000).toFixed(1)}M`,
          icon: DollarSign,
          color: "purple",
          bgColor: "bg-purple-50",
          iconColor: "text-purple-600",
          textColor: "text-purple-900",
        },
        {
          title: "Planning Projects",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
      ]
    } else if (reportType === "travel_logistics") {
      return [
        {
          title: "Total Requests",
          value: summary.total,
          icon: Plane,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Pending Requests",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
        {
          title: "Approved Requests",
          value: summary.approved,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Rejected Requests",
          value: summary.rejected,
          icon: XCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
      ]
    } else {
      return [
        {
          title: "Total Requests",
          value: summary.total,
          icon: FileText,
          color: "blue",
          bgColor: "bg-blue-50",
          iconColor: "text-blue-600",
          textColor: "text-blue-900",
        },
        {
          title: "Pending",
          value: summary.pending,
          icon: Clock,
          color: "yellow",
          bgColor: "bg-yellow-50",
          iconColor: "text-yellow-600",
          textColor: "text-yellow-900",
        },
        {
          title: "Fulfilled",
          value: summary.fulfilled,
          icon: CheckCircle,
          color: "green",
          bgColor: "bg-green-50",
          iconColor: "text-green-600",
          textColor: "text-green-900",
        },
        {
          title: "Delayed",
          value: summary.delayed,
          icon: AlertCircle,
          color: "red",
          bgColor: "bg-red-50",
          iconColor: "text-red-600",
          textColor: "text-red-900",
        },
      ]
    }
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-orange-50/30 to-gray-100 font-sans">
      <HOSidebar visible={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex flex-col flex-1 md:ml-64">
        <Topbar onSidebarToggle={() => setSidebarOpen(true)} />

        {/* Premium Header */}
        <div className="flex-1 p-6 space-y-6 pt-20">
          <div style={{
            background: 'linear-gradient(135deg, #ea580c 0%, #f97316 50%, #fb923c 100%)',
            borderRadius: '20px',
            padding: '32px 36px',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 10px 40px rgba(234, 88, 12, 0.25)',
          }}>
            {/* Decorative orbs */}
            <div style={{
              position: 'absolute', top: '-40px', right: '-40px',
              width: '160px', height: '160px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)',
            }} />
            <div style={{
              position: 'absolute', bottom: '-30px', left: '30%',
              width: '100px', height: '100px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
            }} />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between" style={{ position: 'relative', zIndex: 1 }}>
              <div className="mb-4 lg:mb-0">
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>ANALYTICS CENTER</p>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 800, color: '#fff', margin: 0 }}>
                  {getReportTitle()}
                </h1>
                {selectedProject !== "all" && (
                  <div style={{
                    marginTop: '10px',
                    display: 'inline-flex', alignItems: 'center',
                    padding: '4px 14px', borderRadius: '999px',
                    background: 'rgba(255,255,255,0.2)', color: '#fff',
                    fontSize: '0.8rem', fontWeight: 500, backdropFilter: 'blur(8px)',
                  }}>
                    Filtered by: {projects.find(p => p._id === selectedProject)?.name || "Unknown Project"}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                <div className="relative" ref={exportDropdownRef}>
                  <button
                    onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    style={{
                      display: 'inline-flex', alignItems: 'center',
                      padding: '10px 20px',
                      background: 'rgba(255,255,255,0.2)',
                      backdropFilter: 'blur(12px)',
                      color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                      borderRadius: '12px', fontWeight: 600, fontSize: '0.875rem',
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Report
                    <ChevronDown className="ml-2 w-4 h-4" />
                  </button>

                  {exportDropdownOpen && (
                    <div style={{
                      position: 'absolute', right: 0, marginTop: '8px', width: '200px',
                      borderRadius: '12px', background: '#fff',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      border: '1px solid #e5e7eb', zIndex: 10, overflow: 'hidden',
                    }}>
                      {["csv", "json", "pdf"].map((fmt) => (
                        <button
                          key={fmt}
                          onClick={() => exportReport(fmt)}
                          style={{
                            display: 'flex', alignItems: 'center', width: '100%',
                            padding: '10px 16px', fontSize: '0.875rem',
                            color: '#374151', background: 'transparent',
                            border: 'none', cursor: 'pointer', transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => e.target.style.background = '#f3f4f6'}
                          onMouseLeave={e => e.target.style.background = 'transparent'}
                        >
                          {fmt === "csv" ? <FileSpreadsheet className="mr-2 h-4 w-4" /> : <FileText className="mr-2 h-4 w-4" />}
                          Export as {fmt.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg shadow-sm">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-400 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="text-sm text-red-600 hover:text-red-800 mt-2 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Control Panel — Premium */}
        <div style={{
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          border: '1px solid #e5e7eb',
          marginBottom: '24px',
          overflow: 'hidden',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #fff7ed 0%, #f0f9ff 100%)',
            padding: '14px 24px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center',
          }}>
            <Filter className="w-5 h-5" style={{ color: '#ea580c', marginRight: '10px' }} />
            <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', margin: 0 }}>Report Configuration</h2>
          </div>

          <div style={{ padding: '20px 24px' }}>
            {/* Report Type Tabs — Horizontal */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Report Type</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[
                  { key: "procurement", label: "Procurement", Icon: Package },
                  { key: "travel_logistics", label: "Travel Logistics", Icon: Plane },
                  { key: "requisitions", label: "Requisitions", Icon: FileCheck },
                  { key: "vendor", label: "Vendors", Icon: Users },
                  { key: "contract", label: "Contracts", Icon: FileText },
                  { key: "project_planning", label: "Project Planning", Icon: BarChart3 },
                ].map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => setReportType(key)}
                    style={{
                      display: 'inline-flex', alignItems: 'center',
                      padding: '9px 18px', borderRadius: '10px',
                      fontSize: '0.8125rem', fontWeight: 600,
                      border: reportType === key ? '2px solid #ea580c' : '2px solid #e5e7eb',
                      background: reportType === key ? 'linear-gradient(135deg, #fff7ed, #ffedd5)' : '#fff',
                      color: reportType === key ? '#c2410c' : '#4b5563',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: reportType === key ? '0 2px 8px rgba(234,88,12,0.15)' : 'none',
                    }}
                  >
                    <Icon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {/* Project Filter */}
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Project</label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '1px solid #d1d5db', borderRadius: '10px',
                    fontSize: '0.875rem', color: '#111827',
                    outline: 'none', background: '#fff',
                  }}
                >
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Range */}
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date Range</label>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '1px solid #d1d5db', borderRadius: '10px',
                    fontSize: '0.875rem', color: '#111827',
                    outline: 'none', background: '#fff',
                  }}
                >
                  <option value="current_month">Current Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Refresh Button */}
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  onClick={loadReportData}
                  disabled={loading}
                  style={{
                    width: '100%',
                    background: loading ? '#9ca3af' : 'linear-gradient(135deg, #ea580c, #f97316)',
                    color: '#fff', border: 'none',
                    padding: '10px 20px', borderRadius: '10px',
                    fontWeight: 600, fontSize: '0.875rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(234,88,12,0.25)',
                    transition: 'all 0.2s',
                  }}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Loading...' : 'Refresh Data'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Travel Logistics Dashboard - Integrated */}
        {reportType === "travel_logistics" && (
          <div className="space-y-6">
            {/* Travel Summary Stats */}
            <DashboardSummary summaryData={travelSummary} />

            {/* Travel Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Filter Travel Requests</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <select
                  value={travelFilters.status}
                  onChange={(e) => handleTravelFilterChange({ ...travelFilters, status: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>

                <input
                  type="text"
                  placeholder="Destination"
                  value={travelFilters.destination}
                  onChange={(e) => handleTravelFilterChange({ ...travelFilters, destination: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />

                <select
                  value={travelFilters.dateRange}
                  onChange={(e) => handleTravelFilterChange({ ...travelFilters, dateRange: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">All Dates</option>
                  <option value="upcoming">Upcoming (7 days)</option>
                  <option value="this_month">This Month</option>
                  <option value="past">Past Travels</option>
                </select>
              </div>
            </div>

            {/* Travel Requests List */}
            <div className="grid grid-cols-1 gap-4">
              {travelRequests.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                  <p className="text-gray-600">No travel requests found.</p>
                </div>
              ) : (
                travelRequests.map((request) => (
                  <TravelRequestCard
                    key={request._id}
                    request={request}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onAddItinerary={handleAddItinerary}
                    onReviewReport={handleReviewReport}
                  />
                ))
              )}
            </div>

            {travelLoading && travelRequests.length > 0 && (
              <div className="text-center mt-4">
                <div className="animate-spin h-6 w-6 border-2 rounded-full border-orange-500 border-t-transparent mx-auto"></div>
              </div>
            )}
          </div>
        )}

        {/* KPI Cards — Premium Glassmorphic */}
        {reportType !== "travel_logistics" && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '24px' }}>
            {getKPICards().map((card, index) => (
              <div
                key={index}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  border: '1px solid #f3f4f6',
                  padding: '24px',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'default',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.06)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>{card.title}</p>
                    <p className={card.textColor} style={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>{card.value}</p>
                  </div>
                  <div className={card.bgColor} style={{ padding: '14px', borderRadius: '14px' }}>
                    <card.icon className={card.iconColor} style={{ width: '24px', height: '24px' }} />
                  </div>
                </div>
                <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', fontSize: '0.7rem', color: '#9ca3af' }}>
                  <TrendingUp style={{ width: '12px', height: '12px', marginRight: '4px' }} />
                  Updated just now
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Charts Section — Premium */}
        {reportType !== "travel_logistics" && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px', marginBottom: '24px' }}>
            {/* Status Distribution */}
            <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6', padding: '24px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>Status Distribution</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={currentData.byStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {currentData.byStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Timeline Trend */}
            <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6', padding: '24px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#111827', marginBottom: '20px' }}>Trend Analysis (6 Months)</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={currentData.timeline}>
                    <defs>
                      <linearGradient id="colorFulfilled" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDelayed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="fulfilled"
                      stackId="1"
                      stroke="#10b981"
                      fillOpacity={1}
                      fill="url(#colorFulfilled)"
                      name="Fulfilled"
                    />
                    <Area
                      type="monotone"
                      dataKey="pending"
                      stackId="1"
                      stroke="#f59e0b"
                      fillOpacity={1}
                      fill="url(#colorPending)"
                      name="Pending"
                    />
                    <Area
                      type="monotone"
                      dataKey="delayed"
                      stackId="1"
                      stroke="#ef4444"
                      fillOpacity={1}
                      fill="url(#colorDelayed)"
                      name="Delayed"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Contract Special Tables - Only for contract report */}
        {reportType === "contract" && (
          <div className="space-y-6">
            {/* Expired Contracts Table */}
            <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6', overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg, #fef2f2, #fff7ed)', padding: '16px 24px', borderBottom: '1px solid #fecaca' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#991b1b', margin: 0 }}>Expired Contracts</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const now = new Date()
                      const expiredContracts = currentData.details.filter(item =>
                        item.status === 'expired' ||
                        (item.endDate && new Date(item.endDate) < now)
                      )

                      return expiredContracts.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                            No expired contracts found.
                          </td>
                        </tr>
                      ) : (
                        expiredContracts.map((contract) => (
                          <tr key={contract._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contract.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.vendor}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.endDate ? new Date(contract.endDate).toLocaleDateString() : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.value || "N/A"}
                            </td>
                          </tr>
                        ))
                      )
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Expiring Soon Contracts Table */}
            <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6', overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(135deg, #fffbeb, #fff7ed)', padding: '16px 24px', borderBottom: '1px solid #fde68a' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#92400e', margin: 0 }}>Contracts Expiring Soon (Within 30 Days)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contract</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const now = new Date()
                      const thirtyDaysFromNow = new Date()
                      thirtyDaysFromNow.setDate(now.getDate() + 30)

                      const expiringSoonContracts = currentData.details.filter(item => {
                        if (!item.endDate) return false
                        const endDate = new Date(item.endDate)
                        return endDate > now && endDate <= thirtyDaysFromNow
                      })

                      return expiringSoonContracts.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                            No contracts expiring soon.
                          </td>
                        </tr>
                      ) : (
                        expiringSoonContracts.map((contract) => (
                          <tr key={contract._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {contract.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.vendor}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.endDate ? new Date(contract.endDate).toLocaleDateString() : "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contract.value || "N/A"}
                            </td>
                          </tr>
                        ))
                      )
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Data Table - Not shown for travel logistics or contract report (since we have special tables) */}
        {reportType !== "travel_logistics" && reportType !== "contract" && (
          <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: '1px solid #f3f4f6', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #fff7ed, #f0f9ff)', padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 lg:mb-0">
                  Detailed{" "}
                  {reportType === "procurement"
                    ? "Procurement"
                    : reportType === "requisitions"
                      ? "Requisitions"
                      : reportType === "vendor"
                        ? "Vendor"
                        : reportType === "contract"
                          ? "Contract"
                          : "Project Planning"}{" "}
                  Records
                </h3>

                {/* Search and Filter Controls */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search records..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setCurrentPage(1)
                      }}
                      className="pl-9 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="p-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                  >
                    <option value="all">All Statuses</option>
                    {reportType === "vendor" && (
                      <>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                      </>
                    )}
                    {reportType === "contract" && (
                      <>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="renewing">Renewing</option>
                      </>
                    )}
                    {reportType === "project_planning" && (
                      <>
                        <option value="planning">Planning</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                      </>
                    )}
                    {(reportType === "procurement" || reportType === "requisitions") && (
                      <>
                        <option value="pending">Pending</option>
                        <option value="fulfilled">Fulfilled</option>
                        <option value="delayed">Delayed</option>
                      </>
                    )}
                  </select>
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode("grid")}
                      className={`p-2 ${viewMode === "grid" ? "bg-orange-100 text-orange-800" : "bg-white text-gray-700"}`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode("list")}
                      className={`p-2 ${viewMode === "list" ? "bg-orange-100 text-orange-800" : "bg-white text-gray-700"}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {viewMode === "list" ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Name" :
                          reportType === "contract" ? "Title" :
                            reportType === "project_planning" ? "Project Title" : "Type"}
                      </th>
                      {reportType !== "vendor" && reportType !== "contract" && reportType !== "project_planning" && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Project
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Email" :
                          reportType === "contract" ? "Vendor" :
                            reportType === "project_planning" ? "Status" : "Description"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Created Date" :
                          reportType === "contract" ? "Start Date" :
                            reportType === "project_planning" ? "Start Date" : "Request Date"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" ? "Contract Expiry" :
                          reportType === "contract" ? "End Date" :
                            reportType === "project_planning" ? "End Date" : "Expected Date"}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {reportType === "vendor" || reportType === "contract" || reportType === "project_planning" ? "Value" :
                          reportType === "procurement" ? "Value" : reportType === "requisitions" ? "Estimated Value" : "Cost"}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-8 text-center text-gray-400">
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((item) => (
                        <tr key={item._id || item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item._id || item.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {reportType === "vendor" ? item.name :
                                reportType === "contract" ? item.title :
                                  reportType === "project_planning" ? item.projectTitle : item.type}
                            </span>
                          </td>
                          {reportType !== "vendor" && reportType !== "contract" && reportType !== "project_planning" && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.project || "N/A"}</td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {reportType === "vendor" ? item.email :
                              reportType === "contract" ? item.vendor :
                                reportType === "project_planning" ? item.status : (item.description || item.title || "N/A")}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getStatusIcon(item.status)}
                              <span
                                className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(item.status)}`}
                              >
                                {item.status}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.value || item.cost || "N/A"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6">
                {paginatedData.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No records found.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedData.map((item) => (
                      <div key={item._id || item.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {reportType === "vendor" ? item.name :
                                reportType === "contract" ? item.title :
                                  reportType === "project_planning" ? item.projectTitle : item.type}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {item._id || item.id}
                            </p>
                          </div>
                          <div className="flex items-center">
                            {getStatusIcon(item.status)}
                            <span
                              className={`ml-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(item.status)}`}
                            >
                              {item.status}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          {reportType !== "vendor" && reportType !== "contract" && reportType !== "project_planning" && (
                            <div>
                              <span className="text-gray-500">Project:</span>
                              <span className="ml-2 text-gray-900">{item.project || "N/A"}</span>
                            </div>
                          )}

                          <div>
                            <span className="text-gray-500">
                              {reportType === "vendor" ? "Email:" :
                                reportType === "contract" ? "Vendor:" :
                                  reportType === "project_planning" ? "Status:" : "Description:"}
                            </span>
                            <span className="ml-2 text-gray-900">
                              {reportType === "vendor" ? item.email :
                                reportType === "contract" ? item.vendor :
                                  reportType === "project_planning" ? item.status : (item.description || item.title || "N/A")}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">
                              {reportType === "vendor" ? "Created:" :
                                reportType === "contract" ? "Start Date:" :
                                  reportType === "project_planning" ? "Start Date:" : "Requested:"}
                            </span>
                            <span className="ml-2 text-gray-900">
                              {item.requestDate ? new Date(item.requestDate).toLocaleDateString() : "N/A"}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">
                              {reportType === "vendor" ? "Expiry:" :
                                reportType === "contract" ? "End Date:" :
                                  reportType === "project_planning" ? "End Date:" : "Expected:"}
                            </span>
                            <span className="ml-2 text-gray-900">
                              {item.expectedDate ? new Date(item.expectedDate).toLocaleDateString() : "N/A"}
                            </span>
                          </div>

                          <div>
                            <span className="text-gray-500">Value:</span>
                            <span className="ml-2 text-gray-900 font-medium">
                              {item.value || item.cost || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                          <button className="text-orange-600 hover:text-orange-800 text-sm font-medium flex items-center">
                            <MoreHorizontal className="w-4 h-4 mr-1" />
                            Actions
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{" "}
                      <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredDetails.length)}</span>{" "}
                      of <span className="font-medium">{filteredDetails.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                              ? "z-10 bg-orange-50 border-orange-500 text-orange-600"
                              : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                              }`}
                          >
                            {pageNum}
                          </button>
                        )
                      })}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Travel Modals */}
        <RejectModal
          isOpen={isRejectModalOpen}
          onClose={() => {
            setIsRejectModalOpen(false)
            setSelectedRequestId(null)
          }}
          onSubmit={handleRejectSubmit}
        />

        <AddItineraryModal
          isOpen={isItineraryModalOpen}
          onClose={() => {
            setIsItineraryModalOpen(false)
            setSelectedRequestId(null)
          }}
          onSubmit={handleItinerarySubmit}
        />
        </div>
      </div>
    </div>
  )
}

export default HOCReports